import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import StudentShell from "../components/StudentShell/StudentShell";
import * as programTestsService from "../services/programTests";
import ExerciseRunner, {
  exerciseHasAnswer,
} from "../components/tests/ExerciseRunner";
import "./ProgramTestTake.css";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

export default function ProgramTestTake() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { user, accessToken, effectiveRole, activeStudentId } = useAuth();
  const { language, t } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";

  const isStudent = effectiveRole === "student";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const allowLeaveRef = useRef(false);
  const autosaveTimerRef = useRef(null);

  const getDraftKey = useCallback(
    () => `casaclass:testAnswers:authed:${String(testId)}`,
    [testId],
  );

  const getTypeLabel = (exerciseType) => {
    const t = String(exerciseType || "");
    const defaults = {
      multiple_choice: "Choose",
      true_false: "True / False",
      short_text: "Write",
      long_text: "Write",
      essay: "Essay",
      fill_blanks: "Fill blanks",
      match_pairs: "Match",
      connect_arrow: "Connect",
      select_words: "Select words",
      stimulus: "Read",
      diagram_label: "Label diagram",
      image_label: "Label image",
      drag_drop: "Drag & drop",
      data_interpretation: "Data",
      task: "Task",
    };
    const defaultValue = defaults[t];
    if (!defaultValue) return t;
    return tFn(`testsModule.editor.typeNames.${t}`, {
      defaultValue: tFn(`testsModule.exerciseTypes.${t}`, { defaultValue }),
    });
  };

  const tFn = useCallback(
    (key, options) => {
      try {
        return t(key, options);
      } catch {
        return options?.defaultValue || key;
      }
    },
    [t],
  );

  const questionCount = useMemo(
    () =>
      asArray(test?.exercises).filter(
        (e) => String(e?.exercise_type || "") !== "stimulus",
      ).length,
    [test?.exercises],
  );
  const answeredCount = useMemo(
    () =>
      asArray(test?.exercises)
        .filter((e) => String(e?.exercise_type || "") !== "stimulus")
        .filter((e) => exerciseHasAnswer(e, answers[String(e.id)])).length,
    [answers, test?.exercises],
  );

  const progressPercent = questionCount
    ? Math.round((answeredCount / questionCount) * 100)
    : 0;

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const payload = await programTestsService.fetchProgramTest({
          accessToken: user ? accessToken : undefined,
          testId,
        });
        if (ignore) return;
        setTest(payload);

        // Restore saved draft answers (best-effort).
        try {
          const raw = sessionStorage.getItem(getDraftKey());
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object") setAnswers(parsed);
          }
        } catch {
          // ignore
        }
      } catch (e) {
        if (ignore) return;
        setError(
          e?.message ||
            tFn("testsModule.take.failedLoad", {
              defaultValue: "Failed to load test",
            }),
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [accessToken, getDraftKey, tFn, testId, user]);

  useEffect(() => {
    // Debounced autosave of answers to sessionStorage.
    if (!testId) return undefined;
    if (submitted) return undefined;

    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    autosaveTimerRef.current = window.setTimeout(() => {
      try {
        sessionStorage.setItem(getDraftKey(), JSON.stringify(answers || {}));
      } catch {
        // ignore
      }
    }, 450);

    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [answers, getDraftKey, submitted, testId]);

  const exercises = useMemo(() => asArray(test?.exercises), [test?.exercises]);
  const questionExercises = useMemo(
    () =>
      exercises.filter((e) => String(e?.exercise_type || "") !== "stimulus"),
    [exercises],
  );

  const questionNumberById = useMemo(() => {
    const map = {};
    let n = 0;
    for (const ex of exercises) {
      if (String(ex?.exercise_type || "") === "stimulus") continue;
      n += 1;
      map[String(ex.id)] = n;
    }
    return map;
  }, [exercises]);

  // The "Send" button asks first when questions are still unanswered
  // (the automatic submit when time runs out does not).
  const confirmAndSubmit = () => {
    const missing = Math.max(0, questionCount - answeredCount);
    if (
      missing > 0 &&
      !window.confirm(
        tFn("testsModule.take.confirmUnanswered", {
          defaultValue:
            "You haven't answered {{n}} question(s). Send your answers anyway?",
          n: missing,
        }),
      )
    ) {
      return;
    }
    doSubmit();
  };

  const doSubmit = async () => {
    if (!accessToken) return;

    setSubmitting(true);
    setError("");
    try {
      const payload = {
        answers: exercises.map((ex) => ({
          exercise_id: ex.id,
          answer: answers[String(ex.id)] ?? {},
        })),
      };

      if (user?.role === "parent" && activeStudentId) {
        payload.student_id = activeStudentId;
      }

      await programTestsService.submitProgramTest({
        accessToken,
        testId,
        payload,
      });
      try {
        sessionStorage.removeItem(getDraftKey());
      } catch {
        // ignore
      }
      setSubmitted(true);
    } catch (e) {
      setError(
        e?.message ||
          tFn("testsModule.take.failedSubmit", {
            defaultValue: "Failed to submit test",
          }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (submitted) return undefined;
    if (allowLeaveRef.current) return undefined;

    const message = tFn("testsModule.take.doNotLeave", {
      defaultValue: "Do not leave the page during the test.",
    });

    const onBeforeUnload = (e) => {
      if (allowLeaveRef.current) return;
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    const onPopState = () => {
      if (allowLeaveRef.current) return;
      const ok = window.confirm(
        tFn("testsModule.take.confirmLeave", {
          defaultValue: "Do you want to leave the test?",
        }),
      );
      if (!ok) {
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("popstate", onPopState);
    };
  }, [submitted, tFn]);

  const leaveTest = () => {
    if (
      !window.confirm(
        tFn("testsModule.take.confirmLeave", {
          defaultValue: "Do you want to leave the test?",
        }),
      )
    )
      return;
    allowLeaveRef.current = true;
    navigate("/tests");
  };

  const content = (
    <main className="test-take" dir={dir}>
      <div className="container test-take__container">
        <header className="test-take__header">
          <div>
            <h1 className="test-take__title">
              {test?.title ||
                tFn("testsModule.take.test", { defaultValue: "Test" })}
            </h1>
            <div className="test-take__meta">
              {test?.level_grade ? (
                <span>
                  {tFn("testsModule.common.level", { defaultValue: "Level" })}:{" "}
                  {test.level_grade}
                </span>
              ) : null}
              {test?.duration_minutes ? (
                <span>
                  {tFn("testsModule.take.durationLabel", {
                    defaultValue: "Duration",
                  })}
                  : {test.duration_minutes}{" "}
                  {tFn("testsModule.take.minutesShort", {
                    defaultValue: "min",
                  })}
                </span>
              ) : null}
              {questionCount ? (
                <span>
                  {tFn("testsModule.take.answered", {
                    defaultValue: "Answered:",
                  })}{" "}
                  {answeredCount}/{questionCount}
                </span>
              ) : null}
            </div>
            {questionCount ? (
              <div
                className="test-take__progress"
                aria-label={tFn("testsModule.take.progress", {
                  defaultValue: "Progress",
                })}
              >
                <div
                  className="test-take__progressBar"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            ) : null}

            {!submitted && questionExercises.length ? (
              <div
                className="test-take__nav"
                role="navigation"
                aria-label={tFn("testsModule.take.questionNavigation", {
                  defaultValue: "Question navigation",
                })}
              >
                {questionExercises.map((ex, idx) => {
                  const qNum = idx + 1;
                  const answered = exerciseHasAnswer(
                    ex,
                    answers[String(ex.id)],
                  );
                  return (
                    <button
                      key={ex.id}
                      type="button"
                      className={
                        answered
                          ? "btn ghost test-take__navBtn test-take__navBtn--done"
                          : "btn ghost test-take__navBtn"
                      }
                      onClick={() => {
                        const el = document.getElementById(`q-${ex.id}`);
                        if (el)
                          el.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                      }}
                      aria-label={tFn("testsModule.take.questionLabel", {
                        num: qNum,
                        defaultValue: `Question ${qNum}`,
                      })}
                    >
                      {qNum}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </header>

        {error ? <div className="test-take__error">{error}</div> : null}
        {loading ? (
          <div className="test-take__loading">
            {tFn("testsModule.common.loading", { defaultValue: "Loading..." })}
          </div>
        ) : null}
        {!loading && !test ? (
          <div className="test-take__empty">
            {tFn("testsModule.take.testNotFound", {
              defaultValue: "Test not found.",
            })}
          </div>
        ) : null}

        {!loading && test ? (
          <>
            {test.description ? (
              <section className="test-take__card">
                <p className="test-take__desc">{test.description}</p>
              </section>
            ) : null}

            {submitted ? (
              <section className="test-take__card">
                <h2>
                  {tFn("testsModule.take.submittedTitle", {
                    defaultValue: "Submitted",
                  })}
                </h2>
                <p>
                  {tFn("testsModule.take.submittedBody", {
                    defaultValue: "Your answers were sent to the teacher.",
                  })}
                </p>
                <Link className="btn primary" to="/tests">
                  {tFn("testsModule.take.backToTests", {
                    defaultValue: "Back to tests",
                  })}
                </Link>
              </section>
            ) : (
              <>
                {exercises.map((exercise, index) => (
                  <section
                    className="test-take__card"
                    key={exercise.id}
                    id={`q-${exercise.id}`}
                  >
                    <div className="test-take__questionHead">
                      <div className="test-take__qIndex">
                        {String(exercise.exercise_type || "") === "stimulus"
                          ? "—"
                          : tFn("testsModule.editor.questionN", {
                              defaultValue: "Question {{n}}",
                              n: questionNumberById[String(exercise.id)],
                            })}
                      </div>
                      <div className="test-take__qMeta">
                        <span className="test-take__qType">
                          {getTypeLabel(exercise.exercise_type)}
                        </span>
                      </div>
                    </div>

                    {String(exercise.exercise_type || "") === "stimulus" ? (
                      exercise.prompt ? (
                        <div className="test-take__stimulus">
                          <div className="test-take__stimulusLabel">
                            {tFn("testsModule.take.passage", {
                              defaultValue: "Passage",
                            })}
                          </div>
                          <div className="test-take__stimulusBody">
                            {exercise.prompt}
                          </div>
                        </div>
                      ) : null
                    ) : exercise.prompt ? (
                      <p className="test-take__prompt">{exercise.prompt}</p>
                    ) : null}

                    <ExerciseRunner
                      prefix="test-take"
                      language={language}
                      exercise={exercise}
                      value={answers[String(exercise.id)]}
                      onChange={(next) => {
                        setAnswers((prev) => ({
                          ...prev,
                          [String(exercise.id)]: next,
                        }));
                      }}
                    />
                  </section>
                ))}

                {!exercises.length ? (
                  <section className="test-take__card">
                    <div className="test-take__empty">
                      {tFn("testsModule.take.noExercisesYet", {
                        defaultValue: "No exercises yet.",
                      })}
                    </div>
                  </section>
                ) : null}

                {isStudent ? (
                  <div className="test-take__submit">
                    <button
                      className="btn primary"
                      type="button"
                      onClick={confirmAndSubmit}
                      disabled={submitting || !exercises.length}
                    >
                      {submitting
                        ? tFn("testsModule.common.submitting", {
                            defaultValue: "Submitting...",
                          })
                        : tFn("testsModule.common.submit", {
                            defaultValue: "Submit",
                          })}
                    </button>
                    <button
                      className="btn ghost"
                      type="button"
                      onClick={leaveTest}
                      disabled={submitting}
                    >
                      {tFn("testsModule.common.leave", {
                        defaultValue: "Leave",
                      })}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="test-take__note">
                      {tFn("testsModule.take.previewModeNote", {
                        defaultValue:
                          "Preview mode (teachers/admins cannot submit).",
                      })}
                    </div>
                    <div className="test-take__submit">
                      <button
                        className="btn ghost"
                        type="button"
                        onClick={leaveTest}
                      >
                        {tFn("testsModule.common.leave", {
                          defaultValue: "Leave",
                        })}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        ) : null}
      </div>
    </main>
  );

  if (isStudent) {
    return <StudentShell>{content}</StudentShell>;
  }

  return content;
}
